import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { Modal } from 'flowbite';

import AdminUserGuideSection from './AdminUserGuideSection';

import { generateKey } from 'Helpers/misc';
import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import { Drag, DragAndDrop, Drop } from 'CommonComponents/DragAndDrop';
import Toast from 'CommonComponents/Toast';

import SearchIcon from 'Assets/icons/search.svg';
import XMarkIcon from 'Assets/icons/xmark.svg';

export default function AdminInstructionsIndex(props) {
    const [request, setRequest] = useState(false);
    const [instructions, setInstructions] = useState([]);
    const [deletedInstruction, setDeletedInstruction] = useState(null);
    const [message, setMessage] = useState('');
    const [toastId, setToastId] = useState('instruction-deleted');
    const [isMounted, setIsMounted] = useState(false);
    const [search, setSearch] = useState('');
    const [expandedCategories, setExpandedCategories] = useState({});

    const timeoutRef = useRef(null);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [adminInstructionDeleteModalElement, setAdminInstructionDeleteModalElement] = useState(null);

    const adminInstructionDeleteModalRef = useRef();

    useEffect(() => {
        fetchUserGuides();
        adminInstructionDeleteModalRef && setAdminInstructionDeleteModalElement(new Modal(adminInstructionDeleteModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (isMounted && (search == '' || search == undefined)) {
            updateInstructionsOrder();
        }
    }, [instructions]);

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            fetchUserGuides();
        }, 300);
    }, [search]);

    useEffect(() => {
        if (deletedInstruction) {
            adminInstructionDeleteModalElement && adminInstructionDeleteModalElement.show();
        }
    }, [deletedInstruction]);

    const updateInstructionsOrder = () => {
        const url = props.updateInstructionsOrderUrl;
        const data = { instructions: instructions };

        axios
            .put(url, data)
            .then((res) => {})
            .catch((e) => {
                console.log(e?.response);
            });
    };
    const fetchUserGuides = () => {
        const searchTerm = search != undefined ? search : '';
        let url = addQueryStringToUrl(props.fetchUrl, 'search', searchTerm);

        setRequest(true);
        axios
            .get(url)
            .then((response) => {
                setInstructions(response.data.instructions);
                setIsMounted(true);
                let expanded = JSON.parse(JSON.stringify(expandedCategories));
                response.data.instructions.forEach((category, index) => {
                    const categoryId = category.id;
                    expanded[index] = expanded[index] || {
                        id: categoryId,
                        expanded: true,
                    };
                });
                setExpandedCategories(expanded);
            })
            .catch((e) => {
                console.log(e.response);
            })
            .finally(() => {
                setRequest(false);
            });
    };

    const confirmDeletedInstruction = (instruction) => {
        setDeletedInstruction(instruction);
    };
    const clearDeletedInstruction = () => {
        setDeletedInstruction(null);
        adminInstructionDeleteModalElement && adminInstructionDeleteModalElement.hide();
    };

    const successDeletedHandler = () => {
        setMessage($('Section has been deleted successfully'));
        setTimeout(() => {
            $(`#${toastId}`).toast('show');
        }, 0);
        fetchUserGuides();
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const handleDragEnd = (result) => {
        const { type, source, destination } = result;

        if (!destination) return;

        const sourceCategoryId = source.droppableId?.split('-')[1];
        const destinationCategoryId = destination.droppableId?.split('-')[1];

        // Reordering items
        if (type === 'droppable-item') {
            // If reordering within the same category
            if (sourceCategoryId === destinationCategoryId) {
                const updatedOrder = reorder(instructions.find((category) => category.id == sourceCategoryId).subsections, source.index, destination.index);
                const updatedCategories = instructions.map((category) =>
                    category.id != sourceCategoryId ? category : { ...category, subsections: updatedOrder },
                );

                setInstructions(updatedCategories);
            } else {
                // Dragging to a different category
                const sourceOrder = instructions.find((category) => category.id == sourceCategoryId).subsections;
                const destinationOrder = instructions.find((category) => category.id == destinationCategoryId).subsections;

                const [removed] = sourceOrder.splice(source.index, 1);
                destinationOrder.splice(destination.index, 0, removed);

                destinationOrder[removed] = sourceOrder[removed];
                delete sourceOrder[removed];

                const updatedCategories = instructions.map((category) =>
                    category.id == sourceCategoryId
                        ? { ...category, subsections: sourceOrder }
                        : category.id == destinationCategoryId
                          ? { ...category, subsections: destinationOrder }
                          : category,
                );

                setInstructions(updatedCategories);
            }
        }

        // Reordering categories
        if (type === 'droppable-category') {
            const updatedCategories = reorder(instructions, source.index, destination.index);

            setInstructions(updatedCategories);
        }
    };

    const clearSearch = () => {
        setIsMounted(false);
        setSearch('');
    };
    const changeSearch = (e) => {
        setIsMounted(false);
        const value = e.target.value;
        setSearch(value);
    };

    const toggleExpanded = (categoryIndex) => {
        const expanded = JSON.parse(JSON.stringify(expandedCategories));
        expanded[categoryIndex].expanded = !expanded[categoryIndex].expanded;
        setExpandedCategories(expanded);
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const triggerDeleteInstruction = () => {
        setActionInProgress(true);

        axios
            .delete(props.deleteUrl.replace('INSTRUCTION_ID', deletedInstruction.id))
            .then((response) => {
                successDeletedHandler();
                clearDeletedInstruction();
            })
            .catch((e) => {
                setActionInProgress(false);
                alert(e.response.data.error || $t('Something went wrong'));
                console.log(e.response);
            });
    };

    return (
        <>
            <div className="row mt-3">
                <div className="col mb-4 d-flex justify-content-end">
                    <div className="mr-3">
                        <div className="input-group input-group-rounded">
                            <div className="input-group-prepend">
                                <span className="input-group-text px-3">
                                    <SearchIcon className="tw-w-2.5 tw-h-2.5 tw-text-charcoal-75" />
                                </span>
                            </div>
                            <input type="text" className="form-control live-filter" placeholder={$t('Search')} value={search} onChange={changeSearch} />

                            <span className={`cursor-pointer p-1`} onClick={clearSearch} style={{ visibility: search ? 'visible' : 'hidden' }}>
                                <XMarkIcon className="tw-w-3 tw-h-3" />
                            </span>
                        </div>
                    </div>
                    <Button as="a" variant="primary" className="ml-4" href={createUrl}>
                        {$t('Add new Section')}
                    </Button>
                </div>
            </div>
            <div className="row p-2">
                <div className="col">
                    {instructions && (
                        <DragAndDrop onDragEnd={handleDragEnd}>
                            <Drop id="droppable" type="droppable-category" draggingOverClassNames="tw-bg-blue-25">
                                {instructions.map((category, categoryIndex) => {
                                    const showCategory = expandedCategories[categoryIndex] ? expandedCategories[categoryIndex].expanded : true;
                                    return (
                                        <Drag
                                            key={`category-${category.id}`}
                                            id={`category-${category.id}`}
                                            index={categoryIndex}
                                            dragHandlerClasses="tw-pt-6"
                                            isDragDisabled={search != '' && search != undefined}
                                        >
                                            <AdminUserGuideSection
                                                key={generateKey()}
                                                editUrl={props.editUrl}
                                                item={category}
                                                deleteHandler={() => confirmDeletedInstruction(category)}
                                                toggleExpanded={() => toggleExpanded(categoryIndex)}
                                                expanded={showCategory}
                                                isCategory={true}
                                            />
                                            <div className={showCategory ? '' : 'tw-hidden'}>
                                                <Drop
                                                    key={`subsection-${category.id}`}
                                                    id={`subsection-${category.id}`}
                                                    type="droppable-item"
                                                    draggingOverClassNames="tw-bg-green-25"
                                                >
                                                    {category.subsections?.map((item, itemIndex) => {
                                                        return (
                                                            <Drag
                                                                key={`subsection-${item.id}`}
                                                                id={`subsection-${item.id}`}
                                                                index={itemIndex}
                                                                dragHandlerClasses="tw-pt-6"
                                                            >
                                                                <AdminUserGuideSection
                                                                    key={generateKey()}
                                                                    editUrl={props.editUrl}
                                                                    item={item}
                                                                    deleteHandler={() => confirmDeletedInstruction(item)}
                                                                    isCategory={false}
                                                                />
                                                            </Drag>
                                                        );
                                                    })}
                                                </Drop>
                                            </div>
                                        </Drag>
                                    );
                                })}
                            </Drop>
                        </DragAndDrop>
                    )}
                </div>
            </div>

            <ConfirmationModal
                closeHandler={clearDeletedInstruction}
                successHandler={successDeletedHandler}
                triggerAction={triggerDeleteInstruction}
                cleanStates={cleanConfirmationModalStates}
                allowAction={!!deletedInstruction}
                modalId={'delete-user-guide'}
                modalRef={adminInstructionDeleteModalRef}
                modalElement={adminInstructionDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete the section {{section_title}}?', {
                    section_title: deletedInstruction?.section,
                })}
                actionCompletedMsg=""
                actionFailedMsg=""
                title={$t('Delete Section')}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />
            <Toast id={toastId} message={message}></Toast>
        </>
    );
}

if (document.getElementById('user-guide-admin')) {
    ReactDOM.render(
        <App>
            <AdminInstructionsIndex
                fetchUrl={fetchUrl}
                updateInstructionsOrderUrl={updateInstructionsOrderUrl}
                createUrl={createUrl}
                editUrl={editUrl}
                deleteUrl={deleteUrl}
            />
        </App>,
        document.getElementById('user-guide-admin'),
    );
}
